import { atom, atomFamily, CallbackInterface, DefaultValue, selector } from 'recoil';
import { loadPersistedValue, persistOnSet } from '../effects';
import { DrawFilter, DrawTool, FilterState, MapFilter } from '../../contexts/FilterContext/types';
import { selectedFloorPlanState } from './floorPlan';
import { mapBoundsSelector } from './mapMaintenance';
import { selectedRecordTypeState } from './menuSelections';
import { isEmpty } from 'lodash';
import { configState } from './config';
import { WebUIConfig } from '@terragotech/gen5-shared-components';

// #region Quicksearch
export const quickSearchState = atom<string>({
  key: 'filter.quickSearchState',
  default: '',
  effects: [loadPersistedValue, persistOnSet],
});

export const quickSearchFocusState = atom<boolean>({
  key: 'filter.quickSearchFocusState',
  default: false,
});
// #endregion

// #region Record Filter
export const aiFilterState = atom<Record<string, string>>({
  key: 'filter.aiFilterState',
  default: {},
  effects: [loadPersistedValue, persistOnSet],
});
export const recordFilterState = atomFamily<FilterState, string>({
  key: 'filter.recordFilterState',
  default: _recordType => ({}),
  effects: _recordType => [loadPersistedValue, persistOnSet],
});

export const getRecordFilterCallback =
  ({ snapshot }: CallbackInterface) =>
  (recordType: string): FilterState | undefined => {
    const { contents: candidate, state } = snapshot.getLoadable(recordFilterState(recordType)); // https://recoiljs.org/docs/api-reference/core/Loadable/

    if (state !== 'hasValue' || !candidate) {
      return undefined;
    }

    const config: WebUIConfig = snapshot.getLoadable(configState).contents;

    const def = config?.aggregateDefinitions.find(a => a.name === recordType);
    const cols = def?.view || def?.propertyDefinitions;

    return Object.keys(candidate)
      .filter(k => cols?.some(c => c.field === k || c.field + '-QuickFilter' === k))
      .reduce((prev, k) => ({ ...prev, [k]: candidate[k] }), {});
  };

export const selectedRecordTypeFilterSelector = selector<FilterState>({
  key: 'filter.selectedRecordTypeFilterSelector',
  get: ({ get }) => {
    const selectedRecordType = get(selectedRecordTypeState);
    const candidate = get(recordFilterState(selectedRecordType));
    const config = get(configState);

    const def = config?.aggregateDefinitions.find(a => a.name === selectedRecordType);
    const cols = def?.view || def?.propertyDefinitions;

    return Object.keys(candidate)
      .filter(k => cols?.some(c => c.field === k || c.field + '-QuickFilter' === k))
      .reduce((prev, k) => ({ ...prev, [k]: candidate[k] }), {});
  },
  set: ({ get, reset, set }, newValue) => {
    const selectedRecordType = get(selectedRecordTypeState);
    const filterState = recordFilterState(selectedRecordType);
    if (newValue instanceof DefaultValue) {
      reset(filterState);
    } else {
      set(filterState, newValue);
    }
  },
});

export const selectedRecordTypeFilterCountSelector = selector<number>({
  key: 'filter.selectedRecordTypeFilterCountSelector',
  get: ({ get }) => {
    const selectedRecordTypeFilter = get(selectedRecordTypeFilterSelector);
    return Object.keys(selectedRecordTypeFilter).length;
  },
});
// #endregion

// #region Map Extents
export const mapExtentFilterEnabledState = atom<MapFilter['extentFilterEnabled']>({
  key: 'filter.mapExtentFilterEnabledState',
  default: false,
  effects: [loadPersistedValue, persistOnSet],
});

export const mapFilterSelector = selector<MapFilter>({
  key: 'filter.mapFilterSelector',
  get: ({ get }) => {
    const extentFilterEnabled = get(mapExtentFilterEnabledState);
    const mapBounds = get(mapBoundsSelector);
    const selectedFloorPlanId = get(selectedFloorPlanState)?.id;
    return { extentFilterEnabled, mapBounds, selectedFloorPlanId };
  },
});
// #endregion

// #region Draw Filter
export const drawFilterEnabledState = atom<DrawFilter['enabled']>({
  key: 'filter.drawFilterEnabledState',
  default: false,
});

export const drawFilterFeaturesState = atom<DrawFilter['features']>({
  key: 'filter.drawFilterFeaturesState',
  default: [],
});

export const drawToolEnabledState = atom<DrawTool['enabled']>({
  key: 'filter.drawToolEnabledState',
  default: false,
});

// TODO: [S5-3741] Should this be a readonly selector based on `features.length`?
export const drawToolDrawnState = atom<DrawTool['drawn']>({
  key: 'filter.drawToolDrawnState',
  default: false,
});

export const drawFilterSelector = selector<DrawFilter>({
  key: 'filter.drawFilterSelector',
  get: ({ get }) => {
    const enabled = get(drawFilterEnabledState); // Formerly `drawFilter.apply`.
    const features = get(drawFilterFeaturesState);
    const toolEnabled = get(drawToolEnabledState); // Formerly `drawFilter.enabled`
    return { enabled, features, toolEnabled };
  },
  set: ({ set, reset }, newValue) => {
    if (newValue instanceof DefaultValue) {
      reset(drawFilterEnabledState);
      reset(drawFilterFeaturesState);
      reset(drawToolEnabledState);
      // reset(drawToolDrawnState);  // TODO: [S5-3741] Optimize; currently handled manually.
    } else {
      const { enabled, features, toolEnabled } = newValue;
      set(drawFilterEnabledState, enabled);
      set(drawFilterFeaturesState, features);
      set(drawToolEnabledState, toolEnabled);
      // set(drawToolDrawnState, features.length > 0); // TODO: [S5-3741] Optimize; currently handled manually.
    }
  },
});

// TODO: [S5-3741] Should this set individual atoms? For now, setting selector for closest parity to original method.
export const enableDrawFilterCallback =
  ({ set }: CallbackInterface) =>
  () => {
    set(drawFilterSelector, prev => ({
      ...prev,
      toolEnabled: true,
      enabled: false,
      features: prev.features.map(feature => {
        return { ...feature, properties: { color: '#FF00F5' }, existing: true };
      }),
    }));
  };

export const clearDrawFilterCallback =
  ({ set }: CallbackInterface) =>
  () => {
    set(drawToolDrawnState, false);
    set(drawFilterSelector, {
      toolEnabled: false,
      enabled: false,
      features: [],
    });
  };
// #endregion

// #region Current Filters
export const currentFilterCountSelector = selector<number>({
  key: 'filter.currentFilterCountSelector',
  get: ({ get }) => {
    const selectedRecordTypeFilterCount = get(selectedRecordTypeFilterCountSelector);
    const mapExtentFilterEnabled = get(mapExtentFilterEnabledState);
    const quickSearch = get(quickSearchState);
    const aiFilter = get(aiFilterState);
    const type = get(selectedRecordTypeState);
    return (
      selectedRecordTypeFilterCount +
      (aiFilter[type] ? 1 : 0) +
      (mapExtentFilterEnabled ? 1 : 0) +
      (!isEmpty(quickSearch) ? 1 : 0)
    );
  },
});

export const resetCurrentFiltersCallback =
  ({ reset }: CallbackInterface) =>
  () => {
    reset(selectedRecordTypeFilterSelector);
    reset(mapExtentFilterEnabledState);
    reset(quickSearchState);
    reset(aiFilterState);
  };
// #endregion
